import { Box, Stack } from "@dropbox/dig-foundations";
import { userProfileSettingsAtom } from "@mirage/mosaics/UserProfilePage/atoms";
import { UserProfileCustomizedBackgroundImage } from "@mirage/mosaics/UserProfilePage/background/CustomizedBackground";
import { EditUserProfileBody } from "@mirage/mosaics/UserProfilePage/EditUserProfileBody";
import { UserProfileAvatar } from "@mirage/mosaics/UserProfilePage/UserProfileAvatar";
import { UserProfileBody } from "@mirage/mosaics/UserProfilePage/UserProfileBody";
import { UserProfileHeader } from "@mirage/mosaics/UserProfilePage/UserProfileHeader";
import {
  UserProfileName,
  UserProfileRole,
} from "@mirage/mosaics/UserProfilePage/UserProfileInfo";
import { openURL } from "@mirage/service-platform-actions";
import { TwoColumnGrid } from "@mirage/shared/two-column-grid/TwoColumnGrid";
import { DROPBOX_SETTINGS_PAGE_FOR_DASH_URL } from "@mirage/shared/urls";
import { useSetAtom } from "jotai";
import { useEffect } from "react";

import type { UserInfo } from "@mirage/mosaics/UserProfilePage/types";
import baseStyles from "../BaseProfilePage/BaseProfilePage.module.css";

export const UserProfilePageLayout = ({
  user,
  isEdit,
}: {
  user: UserInfo;
  isEdit: boolean;
}) => {
  const setUserProfileSettings = useSetAtom(userProfileSettingsAtom);

  useEffect(() => {
    // Initialize user profile settings
    setUserProfileSettings(user.user_profile_settings);
  }, [user.uuid]);

  const navigateToDropboxSettings = () => {
    openURL(DROPBOX_SETTINGS_PAGE_FOR_DASH_URL);
  };

  return (
    <Box className={baseStyles.pageContainer} id="user-profile-page-container">
      <UserProfileHeader user={user} showBackButton={true} isEdit={isEdit} />
      <Box as="div" className={baseStyles.contentContainer}>
        <UserProfileCustomizedBackgroundImage user={user} />
        <TwoColumnGrid
          containerRowGap="Macro Large"
          mainRowGap="Macro Small"
          mainChildren={
            <Stack width="100%">
              <Box className={baseStyles.profileContainer}>
                <Box width="100%">
                  <UserProfileAvatar
                    user={user}
                    size="profile"
                    onEditClick={isEdit ? navigateToDropboxSettings : undefined}
                  />
                </Box>
                <Box display="flex" flexDirection="column" width="100%">
                  <UserProfileName user={user} />
                  <Box marginBottom="24">
                    <UserProfileRole user={user} textSize="large" />
                  </Box>
                  {isEdit ? (
                    <EditUserProfileBody user={user} />
                  ) : (
                    <UserProfileBody user={user} />
                  )}
                </Box>
              </Box>
            </Stack>
          }
        />
      </Box>
    </Box>
  );
};
